import type { DiagramPitch } from '@ukinfo/shared';
import { GRID_MAJOR, GRID_MINOR_ZOOM, GRID_STEP, GRID_STEP_FINE, GRID_FINE_ZOOM, PITCH_WIDTH_METERS } from './diagramEdit';
import { PITCH_HEIGHT } from './PitchDiagram';

/**
 * Raster des Skizzeneditors: dezente Linien alle 5 Einheiten (Prozent des Felds), ab 2x Zoom zusaetzlich feine Linien
 * jede Einheit. Reine Anzeige - die Linien fangen keine Zeigerereignisse. Das Rasterrasten selbst steht in `diagramEdit.ts`
 * (`gridStepFor`) und `diagramGroup.ts` (`moveGroup`).
 *
 * Eine Einheit ist ein Prozentpunkt der Feldbreite beziehungsweise der Feldhoehe, wie bei allen Koordinaten der Skizze.
 */

/** Der Pfad der Linien bei den Werten `values` (Feldprozent): senkrechte Linien bei x, waagrechte bei y. */
function linePath(values: readonly number[], height: number): string {
  const vertical = values.map((value) => `M${value} 0V${height}`);
  const horizontal = values.map((value) => `M0 ${Number(((value * height) / 100).toFixed(3))}H100`);
  return [...vertical, ...horizontal].join('');
}

const range = (from: number, to: number, step: number): number[] => {
  const result: number[] = [];
  for (let value = from; value <= to + 1e-9; value += step) result.push(value);
  return result;
};

export function DiagramGrid({ pitch, zoom }: { pitch: DiagramPitch; zoom: number }): JSX.Element {
  const height = PITCH_HEIGHT[pitch];
  const major = range(0, 100, GRID_MAJOR);
  const minorShown = zoom >= GRID_MINOR_ZOOM - 1e-6;
  const minor = minorShown ? range(0, 100, 1).filter((value) => value % GRID_MAJOR !== 0) : [];
  return (
    <g data-testid="diagram-grid" data-minor={minorShown ? 'true' : undefined} pointerEvents="none" fill="none" strokeLinecap="butt">
      {minor.length > 0 && <path d={linePath(minor, height)} stroke="rgba(255,255,255,0.16)" strokeWidth={0.6} vectorEffect="non-scaling-stroke" />}
      <path d={linePath(major, height)} stroke="rgba(255,255,255,0.34)" strokeWidth={0.9} vectorEffect="non-scaling-stroke" />
    </g>
  );
}

/**
 * Text zur Rasterweite in Metern: bei ganzen und halben Feldern gilt die Feldbreite von 68 m, bei der freien Flaeche die Breite,
 * die der Editor annimmt oder die man bei der Masslinie eintraegt. `null`, wenn die Breite nicht bekannt ist.
 */
export function gridWidthText(pitch: DiagramPitch, zoom: number, areaWidthMeters: number | null): string | null {
  const width = pitch === 'AREA' ? areaWidthMeters : PITCH_WIDTH_METERS;
  if (width === null || !(width > 0)) return null;
  const step = zoom >= GRID_FINE_ZOOM - 1e-6 ? GRID_STEP_FINE : GRID_STEP;
  // Auf der x-Achse ist eine Einheit ein Hundertstel der Breite.
  const meters = (step * width) / 100;
  return `${String(Math.round(meters * 100) / 100).replace('.', ',')} m`;
}
